import * as React from "react";
import {
  FieldDefinitionDataType,
  FieldDefinition,
  Variable
} from "@amzn/altar-sds-client";
import {
  Select,
  SpaceBetween,
  FormFieldProps,
  Header,
  Toggle,
} from "@amzn/awsui-components-react";
import { UIField } from "../../../../common/UIField";
import { SmartVariableTextInput } from "./variables/SmartVariableTextInput";
import {
  Condition,
  FieldConfiguration
} from "@amzn/ask-legal-domain";
import { InputTokens } from "../../../../common/InputTokens";

export interface EmailNotification {
  personFieldKey: string;
  emailSubject: string;
  emailBody: string;
  emailAddressList?: string[];
  usePersonField?: boolean;
}

export interface EmailNotificationProps {
  fieldDefinitions: FieldDefinition[];
  emailNotification: EmailNotification;
  workflowNotificationType: Condition.Type;
  fieldConfigurations: FieldConfiguration.Record;
  variables: Variable[];
  onChanged?: (emailNotification: EmailNotification) => void;
}

export const EmailNotification = (props: EmailNotificationProps) => {
  const {
    personFieldKey,
    emailAddressList = [],
    usePersonField = true,
  } = props.emailNotification;

  const emailSubject = convertStorageToDisplayText(props.emailNotification.emailSubject, props.variables, props.fieldDefinitions);
  const emailBody = convertStorageToDisplayText(props.emailNotification.emailBody, props.variables, props.fieldDefinitions);

  const [formFieldPersonFieldKey, setFormFieldPersonFieldKey] =
    React.useState<FormFieldProps>({});
  const [formFieldEmailAliases, setFormFieldEmailAliases] =
    React.useState<FormFieldProps>({});
  const [formFieldEmailSubject, setFormFieldEmailSubject] =
    React.useState<FormFieldProps>({});
  const [formFieldEmailBody, setFormFieldEmailBody] =
    React.useState<FormFieldProps>({});
  const [shouldValidate, setShouldValidate] = React.useState(false);

  React.useEffect(() => {
    if (!emailSubject.trim()) {
      setFormFieldEmailSubject({
        errorText: "Subject is required",
      });
    } else {
      setFormFieldEmailSubject({
        errorText: "",
      });
    }

    if (!emailBody.trim()) {
      setFormFieldEmailBody({
        errorText: "Body is required",
      });
    } else {
      setFormFieldEmailBody({
        errorText: "",
      });
    }

    if (usePersonField) {
      if (!personFieldKey?.trim()) {
        setFormFieldPersonFieldKey({ errorText: "Person field selection is required" });
      } else {
        setFormFieldPersonFieldKey({ errorText: "" });
      }
      setFormFieldEmailAliases({ errorText: "" });
    } else {
      if (shouldValidate && emailAddressList.length === 0) {
        setFormFieldEmailAliases({ errorText: "At least one email alias must be selected" });
      } else {
        setFormFieldEmailAliases({ errorText: "" });
      }
      setFormFieldPersonFieldKey({ errorText: "" });
    }
  }, [usePersonField, personFieldKey, emailSubject, emailBody, emailAddressList, shouldValidate]);

  const handlePersonFieldKeyChange = (value: string) => {
    props.onChanged?.({
      ...props.emailNotification,
      personFieldKey: value,
    });
  };

  const handleEmailSubjectChange = (value: string) => {
    props.onChanged?.(
      { ...props.emailNotification, emailSubject: value }
    );
  };

  const handleEmailBodyChange = (value: string) => {
    props.onChanged?.(
      { ...props.emailNotification, emailBody: value }
    );
  };

  const handleEmailAliasesChange = (aliases: string[]) => {
    setShouldValidate(true);
    props.onChanged?.({
      ...props.emailNotification,
      emailAddressList: aliases,
    });
  };

  const handleNotificationModeToggle = (checked: boolean) => {
    props.onChanged?.({
      ...props.emailNotification,
      usePersonField: checked,
      personFieldKey: checked ? personFieldKey : "",
      emailAddressList: checked ? [] : emailAddressList,
    });
  };
  return (
    <SpaceBetween direction="vertical" size="xxs">
      <Header headingTagOverride="h4">Send Notification</Header>

      <UIField.CustomField
        name="Notification Mode"
        description="Choose how to select recipients"
        child={
          <Toggle checked={usePersonField} onChange={(e) => handleNotificationModeToggle(e.detail.checked)}>
            Use Person Field
          </Toggle>
        }
      />

      {usePersonField ? (
        <UIField.CustomField
          name="Person"
          description="Please select the person field to whom you want to send the notification"
          errorText={formFieldPersonFieldKey.errorText}
          child={
            <Select
              options={props.fieldDefinitions
                ?.filter(
                  (c) =>
                    (c.dataType === FieldDefinitionDataType.IdentityRef ||
                      c.dataType === FieldDefinitionDataType.multiIdentityRef) &&
                    !c.deprecated
                )
                .map((d) => ({
                  value: d.fieldKey,
                  label: d.displayName,
                }))}
              selectedOption={{
                value: personFieldKey,
                label: props.fieldDefinitions?.find(
                  (f) => f.fieldKey === personFieldKey
                )?.displayName,
              }}
              onChange={(e) =>
                handlePersonFieldKeyChange(e.detail.selectedOption.value!)
              }
            />
          }
        />
      ) : (
        <UIField.CustomField
          name="Email Addresses"
          description="Enter one or more email aliases or full email addresses"
          errorText={formFieldEmailAliases.errorText}
          child={
            <InputTokens
              values={emailAddressList}
              setValues={handleEmailAliasesChange}
            />
          }
        />
      )}

      <SpaceBetween direction="vertical" size="xxs">
        <Header headingTagOverride="h4">Include a custom message</Header>
          <UIField.CustomField
            name="Subject"
            errorText={formFieldEmailSubject.errorText}
            child={
              <SmartVariableTextInput
                value={emailSubject}
                variableList={props.variables}
                fieldDefinitions={props.fieldDefinitions}
                onChange={(value) => handleEmailSubjectChange(value)}
              />
            }
          />

          <UIField.CustomField
            name="Body"
            errorText={formFieldEmailBody.errorText}
            child={
              <SmartVariableTextInput
                value={emailBody}
                variableList={props.variables}
                fieldDefinitions={props.fieldDefinitions}
                onChange={(value) => handleEmailBodyChange(value)}
              />
            }
          />
      </SpaceBetween>
    </SpaceBetween>
  );
};


function convertStorageToDisplayText(text: string, variables: Variable[], fieldDefinitions: FieldDefinition[]) {
  const regex = /\$\{context\.([^}]+)\}/g;
  const outputString = text.replace(regex, (match) => {
      const varName = match.split("context.")[1].split("}")[0];
      const foundVariable = variables
          .find(variable => variable.name === varName);
      if (!foundVariable) {
          console.info("no matching var for", match);
          return "";
      }
      const displaySuffix = foundVariable?.value.includes("oldItem") ? "OldValue" : "Value";
      const fieldKey = foundVariable?.value.split("valuesMap['")[1].split("']")[0];
      const fieldDef = fieldDefinitions.find(d => d.fieldKey === fieldKey);
      return `{${fieldDef.displayName}-${displaySuffix}}`;
  });
  if (outputString[0] === "`" && outputString[outputString.length - 1] === "`") {
      return outputString.slice(1, outputString.length - 1);
  } else {
      return outputString;
  }
}